import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
// material
import { alpha, useTheme, styled } from '@mui/material/styles';
import { Box, List, Collapse, ListItemText, ListItemIcon, ListItemButton } from '@mui/material';
import Badge from '@mui/material/Badge';
//
import { getSessionCount } from '../common/api-service';
import Iconify from './Iconify';

import { ThemeContext } from '../App';


// ----------------------------------------------------------------------

const ListItemStyle = styled((props) => <ListItemButton disableGutters {...props} />)(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  textTransform: 'capitalize',
  color: theme.palette.text.secondary,
  borderRadius: theme.shape.borderRadius,
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  color: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

// ----------------------------------------------------------------------

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
};

function NavItem({ item, active}) {
  const theme = useTheme();


  const {sitterAllocationCount, setSitterAllocationCount} = useContext(ThemeContext);
  const {cancellationRequestCount, setCancellationRequestCount} = useContext(ThemeContext);
  const {rescheduleRequestCount, setRescheduleRequestCount} = useContext(ThemeContext);

  const isActiveRoot = active(item.path);

  const { title, path, icon, info, children } = item;

  const [open, setOpen] = useState(isActiveRoot);

  const handleOpen = () => {
    setOpen((prev) => !prev);
    return getSessionCount().then(
      (res) => {
        const [requestedSessionsCount, rescheduledRequestsCount, cancellationRequestsCount] = res.data.data;
        setSitterAllocationCount(requestedSessionsCount.requested_sessions_count);
        setRescheduleRequestCount(rescheduledRequestsCount.rescheduled_requests_count);
        setCancellationRequestCount(cancellationRequestsCount.cancellation_requests_count);
      },
      (err) => {
        console.log('err data: ', err);
      }
    );
  };

  const activeRootStyle = {
    color: 'primary.main',
    fontWeight: 'fontWeightMedium',
    bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
  };

  const activeSubStyle = {
    color: 'text.primary',
    fontWeight: 'fontWeightMedium',
  };

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle),
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
          <ListItemText disableTypography primary={title} />
          {info && info}
          <Iconify
            icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItemStyle>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((item) => {
              const { title, path } = item;
              const isActiveSub = active(path);

              return (
                <ListItemStyle
                  key={title}
                  component={RouterLink}
                  to={path}
                  sx={{
                    ...(isActiveSub && activeSubStyle),
                  }}
                >
                  <ListItemIconStyle>
                    <Box
                      component="span"
                      sx={{
                        width: 4,
                        height: 4,
                        display: 'flex',
                        borderRadius: '50%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'text.disabled',
                        transition: (theme) => theme.transitions.create('transform'),
                        ...(isActiveSub && {
                          transform: 'scale(2)',
                          bgcolor: 'primary.main',
                        }),
                      }}
                    />
                  </ListItemIconStyle>

                  {title === 'Reschedule Request' ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <ListItemText disableTypography primary={title} style={{ marginRight: '20px' }} />
                      <Badge badgeContent={rescheduleRequestCount} color="primary" />
                      {/* <Badge badgeContent={"3"} color="primary" /> */}
                    </div>
                  ) : (
                    ''
                  )}

                  {title === 'Sitter Allocations' ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <ListItemText disableTypography primary={title} style={{ marginRight: '20px' }} />
                        <Badge badgeContent={sitterAllocationCount} color="primary" />
                        {/* <Badge badgeContent={"1"} color="primary" /> */}
                      </div>
                    </>
                  ) : (
                    ''
                  )}

                  {title === "Cancellation Requests" ? (

                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <ListItemText disableTypography primary={title} style={{ marginRight: '20px' }} />
                        <Badge badgeContent={cancellationRequestCount} color="primary" />
                        {/* <Badge badgeContent={"2"} color="primary" /> */}
                      </div>
                    </>
                  ) : (
                    ''
                  )}

                  {title !== 'Reschedule Request' && title !== 'Sitter Allocations' && title !== 'Cancellation Requests' ? (
                    <ListItemText disableTypography primary={title} />
                  ) : (
                    ''
                  )}
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      sx={{
        ...(isActiveRoot && activeRootStyle),
      }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
      <ListItemText disableTypography primary={title} />
      {info && info}
      {/* {children && (
        <Badge badgeContent={children.value} color={children.color}>
          
          <span>{children.value}</span>
        </Badge>
      )} */}
    </ListItemStyle>
  );
}

NavSection.propTypes = {
  navConfig: PropTypes.array,
};

export default function NavSection({ navConfig, ...other }) {
  const { pathname } = useLocation();

  const match = (path) => (path ? !!matchPath({ path, end: false }, pathname) : false);

  return (
    <Box {...other}>
      <List disablePadding sx={{ p: 1 }}>
        {navConfig.map((item) => (
          <NavItem key={item.title} item={item} active={match} />
        ))}
      </List>
    </Box>
  );
}
